export const serviceStates = {
  kentucky: {
    name: "Kentucky",
    description: "Statewide coverage including Louisville, Lexington, and NKY.",
    seoDescription:
      "Emergency logistics, de-icing salt supply, and wholesale plant sourcing across Kentucky.",
    longDescription:
      "Kentucky's unpredictable winter weather and diverse landscaping needs require a reliable logistics partner. From the Ohio River Valley to the Appalachian foothills, we supply premium de-icing salt and wholesale plant materials to keep Kentucky businesses running safely and beautifully.",
    cities: [
      "Louisville",
      "Lexington",
      "Bowling Green",
      "Owensboro",
      "Covington",
      "Richmond",
      "Georgetown",
      "Florence",
      "Nicholasville",
      "Elizabethtown",
      "Frankfort",
      "Paducah",
      "Hopkinsville",
      "Henderson",
      "Jeffersontown",
      "Independence",
      "Ashland",
      "Madisonville",
      "Winchester",
      "Murray",
      "St. Matthews",
      "Fort Thomas",
      "Danville",
      "Newport",
      "Shively",
      "Shelbyville",
      "Glasgow",
      "Berea",
      "Bardstown",
      "Shepherdsville",
      "Somerset",
    ],
  },
  ohio: {
    name: "Ohio",
    description: "Cincinnati, Dayton, Columbus, and surrounding areas.",
    seoDescription:
      "High-volume salt supply and emergency freight services across the Buckeye State.",
    longDescription:
      "With harsh lake-effect snows in the north and icy conditions statewide, Ohio demands robust winter logistics. We provide high-volume salt supply and emergency freight services across the Buckeye State, alongside premium nursery stock sourcing for Ohio's thriving green industry.",
    cities: [
      "Columbus",
      "Cleveland",
      "Cincinnati",
      "Toledo",
      "Akron",
      "Dayton",
      "Parma",
      "Canton",
      "Youngstown",
      "Lorain",
      "Hamilton",
      "Springfield",
      "Kettering",
      "Elyria",
      "Lakewood",
      "Cuyahoga Falls",
      "Middletown",
      "Newark",
      "Euclid",
      "Mansfield",
      "Mentor",
      "Beavercreek",
      "Strongsville",
      "Fairfield",
      "Dublin",
      "Warren",
      "Findlay",
      "Lancaster",
      "Lima",
      "Huber Heights",
      "Westerville",
      "Marion",
      "Brunswick",
      "Boardman",
      "Delaware",
      "Stow",
      "Gahanna",
      "Massillon",
      "North Olmsted",
      "Fairborn",
      "Westlake",
      "Bowling Green",
      "North Royalton",
      "Garfield Heights",
      "Shaker Heights",
      "Mason",
      "Kent",
      "Troy",
      "Medina",
      "Wooster",
      "Barberton",
      "Zanesville",
      "Athens",
      "Centerville",
      "Hilliard",
      "Reynoldsburg",
      "Riverside",
      "Solon",
      "Avon Lake",
      "Wadsworth",
      "Chillicothe",
      "Sandusky",
    ],
  },
  indiana: {
    name: "Indiana",
    description: "Indianapolis, Evansville, Fort Wayne, and statewide.",
    seoDescription:
      "Reliable bulk salt delivery and wholesale plant sourcing across the Hoosier State.",
    longDescription:
      "Indiana's central location makes it a critical hub for logistics. Whether you need bulk salt delivery to combat Midwest winter storms or wholesale plant sourcing for commercial landscaping projects, we provide 24/7 dispatch and reliable supply chains across the Hoosier State.",
    cities: [
      "Indianapolis",
      "Fort Wayne",
      "Evansville",
      "South Bend",
      "Carmel",
      "Fishers",
      "Bloomington",
      "Hammond",
      "Gary",
      "Lafayette",
      "Muncie",
      "Terre Haute",
      "Kokomo",
      "Noblesville",
      "Anderson",
      "Greenwood",
      "Elkhart",
      "Mishawaka",
      "Lawrence",
      "Jeffersonville",
      "Columbus",
      "Portage",
      "Richmond",
      "New Albany",
      "Merrillville",
      "Westfield",
      "Goshen",
      "Valparaiso",
      "Michigan City",
      "West Lafayette",
      "Marion",
      "East Chicago",
      "Plainfield",
      "Schererville",
      "Hobart",
      "Crown Point",
      "Zionsville",
      "Franklin",
      "Highland",
      "Munster",
      "Brownsburg",
      "Clarksville",
      "La Porte",
      "Seymour",
      "Shelbyville",
      "Logansport",
      "Vincennes",
      "New Castle",
      "Huntington",
      "Frankfort",
    ],
  },
  tennessee: {
    name: "Tennessee",
    description: "Nashville, Knoxville, Memphis, and statewide.",
    seoDescription:
      "Emergency freight and dependable winter de-icing salt across Tennessee.",
    longDescription:
      "From the Smoky Mountains to the Mississippi River, Tennessee's varied terrain requires specialized logistics. We support the Volunteer State with emergency freight, dependable winter de-icing salt, and high-quality plant sourcing for its rapidly growing metropolitan areas.",
    cities: [
      "Nashville",
      "Memphis",
      "Knoxville",
      "Chattanooga",
      "Clarksville",
      "Murfreesboro",
      "Franklin",
      "Jackson",
      "Johnson City",
      "Bartlett",
      "Hendersonville",
      "Kingsport",
      "Smyrna",
      "Collierville",
      "Cleveland",
      "Spring Hill",
      "Gallatin",
      "Brentwood",
      "Columbia",
      "Lebanon",
      "Mount Juliet",
      "Cookeville",
      "Morristown",
      "Oak Ridge",
      "Maryville",
      "Bristol",
      "Farragut",
      "Shelbyville",
      "East Ridge",
      "Tullahoma",
      "Springfield",
      "Goodlettsville",
      "Sevierville",
      "Dickson",
      "Greeneville",
      "Athens",
      "Clinton",
      "Crossville",
      "McMinnville",
    ],
  },
};

export function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^\w-]+/g, "");
}

export function unslugify(slug: string) {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getCityIntro(city: string, state: string) {
  const templates = [
    `As a premier logistics provider in ${city}, we understand the unique challenges of operating in ${state}. Our 24/7 dispatch ensures your commercial operations never miss a beat, providing rapid response times when you need materials most.`,
    `Serving the ${city} community, we deliver reliable bulk salt and wholesale plants directly to your job sites across ${state}. We partner with local contractors to streamline their supply chain and reduce downtime.`,
    `Businesses in ${city} trust us for emergency freight and seasonal supplies. We keep ${state} moving, no matter the weather, with our dedicated fleet and extensive network of supply partners.`,
    `From rapid salt deployment to commercial plant sourcing, our dedicated logistics team serves ${city} and the greater ${state} region with unmatched reliability and competitive wholesale pricing.`,
  ];
  const index = city.length % templates.length;
  return templates[index];
}

export function getCityContent(city: string, state: string) {
  return {
    title: `Logistics & Supply in ${city}, ${state}`,
    description: `Emergency logistics, de-icing salt supply, and wholesale plant sourcing in ${city}, ${state}.`,
    intro: getCityIntro(city, state),
  };
}
